import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Trophy, X, RotateCcw, Crosshair } from 'lucide-react';

const G = 400; // gravity in px/s²
const DT = 1 / 60;

export function TargetShooterActivity({ mission, onComplete }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const projRef = useRef(null);
  const [qIdx, setQIdx] = useState(0);
  const [angle, setAngle] = useState(45);
  const [power, setPower] = useState(50);
  const [score, setScore] = useState(0);
  const [firing, setFiring] = useState(false);
  const [hitResult, setHitResult] = useState(null);
  const [done, setDone] = useState(false);
  const doneRef = useRef(false);

  const questions = mission.questions || [];
  const q = questions[qIdx] || {};

  const getTargets = useCallback(() => {
    if (!q.options) return [];
    const W = canvasRef.current?.width || 700;
    const count = Math.min(q.options.length, 4);
    const spacing = (W - 150) / (count + 1);
    return q.options.slice(0, count).map((opt, i) => ({
      label: opt,
      x: 100 + spacing * (i + 1),
      y: 0,
      w: 50,
      correct: opt === q.answer,
    }));
  }, [q]);

  const drawScene = useCallback((ctx, W, H, targets, proj, trail) => {
    ctx.fillStyle = '#080c15';
    ctx.fillRect(0, 0, W, H);

    // Grid
    ctx.strokeStyle = 'rgba(100,150,255,0.03)';
    ctx.lineWidth = 1;
    for (let i = 0; i < W; i += 60) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, H); ctx.stroke(); }
    for (let i = 0; i < H; i += 60) { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(W, i); ctx.stroke(); }

    // Ground
    const groundY = H - 40;
    ctx.fillStyle = '#1a3a1a';
    ctx.fillRect(0, groundY, W, 40);
    ctx.strokeStyle = '#2a5a2a';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(0, groundY); ctx.lineTo(W, groundY); ctx.stroke();

    // Distance markers
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    for (let i = 100; i < W; i += 100) {
      ctx.fillText(`${i}m`, i, groundY + 15);
      ctx.beginPath(); ctx.moveTo(i, groundY); ctx.lineTo(i, groundY + 5); ctx.stroke();
    }

    // Cannon
    const cx = 40, cy = groundY;
    const aRad = (angle * Math.PI) / 180;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-aRad);
    ctx.fillStyle = '#4a5568';
    ctx.fillRect(-5, -6, 35, 12);
    ctx.restore();
    ctx.beginPath();
    ctx.arc(cx, cy, 12, 0, Math.PI * 2);
    ctx.fillStyle = '#374151';
    ctx.fill();
    ctx.strokeStyle = '#6b7280';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Angle indicator
    ctx.fillStyle = '#eab308';
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`${angle}°`, cx + 20, cy - 20);

    // Targets
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    targets.forEach(t => {
      const tx = t.x, ty = groundY;
      // Pole
      ctx.strokeStyle = '#6b7280';
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(tx, ty - 60); ctx.stroke();
      // Board
      ctx.beginPath();
      ctx.arc(tx, ty - 70, 25, 0, Math.PI * 2);
      ctx.fillStyle = hitResult && t.correct ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.15)';
      ctx.fill();
      ctx.strokeStyle = hitResult && t.correct ? '#22c55e' : 'rgba(239,68,68,0.5)';
      ctx.lineWidth = 2;
      ctx.stroke();
      // Inner circle
      ctx.beginPath();
      ctx.arc(tx, ty - 70, 12, 0, Math.PI * 2);
      ctx.fillStyle = hitResult && t.correct ? 'rgba(34,197,94,0.5)' : 'rgba(255,255,255,0.08)';
      ctx.fill();
      // Label
      ctx.fillStyle = '#e0e0e0';
      ctx.font = '11px sans-serif';
      const label = t.label.length > 12 ? t.label.slice(0, 11) + '...' : t.label;
      ctx.fillText(label, tx, ty - 95);
    });

    // Trail
    if (trail && trail.length > 1) {
      ctx.beginPath();
      ctx.moveTo(trail[0].x, trail[0].y);
      for (let i = 1; i < trail.length; i++) ctx.lineTo(trail[i].x, trail[i].y);
      ctx.strokeStyle = 'rgba(250,204,21,0.4)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Projectile
    if (proj) {
      ctx.beginPath();
      ctx.arc(proj.x, proj.y, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#facc15';
      ctx.fill();
      ctx.strokeStyle = '#eab308';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }, [angle, hitResult]);

  // Initial draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.parentElement.clientWidth;
    canvas.width = W;
    canvas.height = 350;
    const ctx = canvas.getContext('2d');
    drawScene(ctx, W, 350, getTargets(), null, null);
  }, [qIdx, angle, drawScene, getTargets]);

  // Redraw on angle/power change
  useEffect(() => {
    if (firing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    drawScene(ctx, canvas.width, canvas.height, getTargets(), null, null);
  }, [angle, power, firing, drawScene, getTargets]);

  const fire = () => {
    if (firing || done) return;
    setFiring(true);
    setHitResult(null);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const groundY = H - 40;
    const targets = getTargets();

    const aRad = (angle * Math.PI) / 180;
    const v = power * 8;
    const vx = v * Math.cos(aRad);
    const vy = v * Math.sin(aRad);

    let t = 0;
    const trail = [];
    const ox = 40, oy = groundY;

    const animate = () => {
      t += DT * 2;
      const px = ox + vx * t;
      const py = oy - (vy * t - 0.5 * G * t * t);

      trail.push({ x: px, y: py });
      drawScene(ctx, W, H, targets, { x: px, y: py }, trail);

      if (py >= groundY && t > 0.05) {
        // Landed - check hit
        let hit = null;
        targets.forEach(tgt => {
          if (Math.abs(px - tgt.x) < 35) hit = tgt;
        });

        if (hit) {
          setHitResult(hit.correct ? 'correct' : 'wrong');
          if (hit.correct) setScore(s => s + 1);
        } else {
          setHitResult('miss');
        }

        setTimeout(() => {
          setFiring(false);
          if (qIdx + 1 < questions.length) {
            setQIdx(i => i + 1);
            setHitResult(null);
          } else {
            setDone(true);
            const finalScore = score + (hit?.correct ? 1 : 0);
            if (!doneRef.current && finalScore >= questions.length * 0.5) {
              doneRef.current = true;
              onComplete({ type: 'target-shooter', hits: finalScore, total: questions.length });
            }
          }
        }, 1500);
        return;
      }

      if (px > W + 50 || py < -200) {
        setHitResult('miss');
        setTimeout(() => {
          setFiring(false);
          if (qIdx + 1 < questions.length) {
            setQIdx(i => i + 1);
            setHitResult(null);
          } else {
            setDone(true);
            if (!doneRef.current && score >= questions.length * 0.5) {
              doneRef.current = true;
              onComplete({ type: 'target-shooter', hits: score, total: questions.length });
            }
          }
        }, 1000);
        return;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
  };

  const restart = () => {
    setQIdx(0); setScore(0); setFiring(false); setHitResult(null); setDone(false); doneRef.current = false;
    setAngle(45); setPower(50);
  };

  useEffect(() => { return () => cancelAnimationFrame(rafRef.current); }, []);

  if (done) {
    const passed = score >= questions.length * 0.5;
    return (
      <div className="text-center py-10" data-testid="target-shooter-result">
        <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {passed ? <Trophy className="w-10 h-10 text-green-400" /> : <X className="w-10 h-10 text-red-400" />}
        </div>
        <h3 className="text-xl font-bold mb-2">{passed ? 'Sharpshooter!' : 'Keep Practicing!'}</h3>
        <p className="text-sm text-muted-foreground mb-4">Hit {score}/{questions.length} correct targets</p>
        <Button onClick={restart} data-testid="target-restart-btn"><RotateCcw className="w-4 h-4 mr-2" /> Try Again</Button>
      </div>
    );
  }

  return (
    <div data-testid="target-shooter-activity">
      <div className="flex items-center justify-between mb-3">
        <Badge variant="outline" className="text-xs">Q{qIdx + 1}/{questions.length}</Badge>
        <p className="text-sm font-semibold flex-1 text-center">{q.question}</p>
        <Badge variant="secondary" className="text-xs"><Crosshair className="w-3 h-3 mr-1" />{score} hits</Badge>
      </div>

      {hitResult && (
        <div className={`text-center text-sm font-bold mb-2 ${hitResult === 'correct' ? 'text-green-400' : 'text-red-400'}`}>
          {hitResult === 'correct' ? 'Direct Hit!' : hitResult === 'wrong' ? `Wrong target! Answer: ${q.answer}` : `Missed! Answer: ${q.answer}`}
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-border/30 bg-muted dark:bg-[#080c15] mb-4">
        <canvas ref={canvasRef} className="w-full block" style={{ height: 350 }} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-3 rounded-xl border border-border/30 bg-card">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Angle</span>
            <span className="font-bold text-primary">{angle}°</span>
          </div>
          <input type="range" min="5" max="85" value={angle} onChange={e => setAngle(+e.target.value)}
            disabled={firing} className="w-full accent-primary" data-testid="target-angle-slider" />
        </div>
        <div className="p-3 rounded-xl border border-border/30 bg-card">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Power</span>
            <span className="font-bold text-orange-400">{power} m/s</span>
          </div>
          <input type="range" min="10" max="90" value={power} onChange={e => setPower(+e.target.value)}
            disabled={firing} className="w-full accent-orange-400" data-testid="target-power-slider" />
        </div>
        <div className="flex items-center justify-center gap-2">
          <Button onClick={fire} disabled={firing} size="lg" className="flex-1" data-testid="target-launch-btn">
            <Crosshair className="w-4 h-4 mr-2" /> Launch
          </Button>
          <Button variant="outline" size="icon" onClick={restart} data-testid="target-reset-btn">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
